import React from 'react';
import {
  Typography, Box, styled, useTheme,
} from '@material-ui/core';

import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SleepioWellnessOnboardingParams } from '../SleepioWellnessOnboardingPage';

import sparkTheme from '@/themes/sparkTheme';

import { Button } from '@/components';
import { isSpark as isSparkHelper } from '@/utils/productHelpers';

const CriteriaWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginTop: 8,
});

const List = styled(Box)(({ theme }) => ({
  margin: 0,
  paddingLeft: theme.spacing(1.5),
  paddingRight: 0,
  listStyle: 'disc',
  listStylePosition: 'outside',
  '& li': {
    marginBottom: theme.spacing(1),
  },
  '& li::marker': {
    color: theme.palette.secondary.main,
  },
}));

type Props = {
  // Only PreOnboardingPage passes this - a full reload there would re-roll the experiment arm.
  onTryAgain?: () => void;
};

const SleepioCoverageRejection: React.FC<Props> = ({ onTryAgain }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { product } = useParams<SleepioWellnessOnboardingParams>();
  const isSpark = isSparkHelper(product);

  const supportEmailKey = isSpark
    ? 'coverage.spark.patient.info_popup.email_spark'
    : 'coverage.sleepio.rejection.support.email';

  const supportEmail = t(supportEmailKey, {
    defaultValue: t(isSpark
      ? 'coverage.spark.patient.info_popup.email_spark'
      : 'coverage.sleepio.rejection.support.email'),
  });

  const handleReload = (): void => {
    if (onTryAgain) {
      onTryAgain();
      return;
    }
    // This will reload the page so that the form is displayed again
    window.location.reload();
  };

  return (
    <>
      <Typography variant="h3" style={{ marginBottom: 8, marginTop: 30 }}>
        {t('coverage.sleepio.rejection.main_title')}
      </Typography>
      <Typography style={{ marginBottom: 8 }}>
        <Typography>{t('coverage.sleepio.rejection.not_eligible')}</Typography>
      </Typography>
      <Typography>{t('coverage.sleepio.rejection.contact')}</Typography>
      <CriteriaWrapper>
        <Typography variant="h3">
          {t('coverage.sleepio.rejection.following_apply.title')}
        </Typography>
        <List component="ul">
          <li>
            <Typography>
              {t('coverage.sleepio.rejection.following_apply.moved_recently')}
            </Typography>
          </li>
          <li>
            <Typography>
              {t(
                'coverage.sleepio.rejection.following_apply.using_full_name_used',
              )}
            </Typography>
          </li>
          <li>
            <Typography>
              {t(
                'coverage.sleepio.rejection.following_apply.birth_date_format',
              )}
            </Typography>
          </li>
        </List>
      </CriteriaWrapper>
      <Button
        color="primary"
        variant="contained"
        data-testid="submit-button"
        onClick={handleReload}
        style={{
          borderRadius: 100,
          width: '100%',
          marginBottom: 25,
          marginTop: 15,
          ...(isSpark
            ? {
              backgroundColor: sparkTheme.palette.primary.main,
              color: sparkTheme.palette.getContrastText(
                sparkTheme.palette.primary.main,
              ),
            }
            : {}),
        }}
      >
        {t(
          isSpark
            ? 'coverage.spark.patient.retry_spark_registration'
            : 'coverage.sleepio.rejection.retry_sleepio_registration',
        )}
      </Button>
      <Typography>
        {t('coverage.sleepio.rejection.support.text_1')}
        {' '}
        <a
          href={`mailto:${supportEmail}`}
          style={{ color: theme.palette.text.primary }}
        >
          {supportEmail}
        </a>
        {t('coverage.sleepio.rejection.support.text_2')}
      </Typography>
      <Typography variant="h3" style={{ marginTop: 25 }}>
        {t('coverage.sleepio.rejection.having_emergency')}
      </Typography>
      <Typography>
        {t('coverage.sleepio.rejection.find_resources')}
        {' '}
        <a
          href={`/${(product || 'sleepio').toLowerCase()}/resources`}
          style={{ color: theme.palette.text.primary }}
        >
          {t('coverage.sleepio.rejection.here')}
        </a>
        .
      </Typography>
    </>
  );
};

export default SleepioCoverageRejection;
